/**
 * uploadJobManager.ts
 * 
 * Handles the lifecycle management of S3 upload jobs with concurrency control,
 * worker queue processing, and integration with S3Service and file validation.
 */

import { S3Service } from './S3Service';
import { validateFileBatch, constructSafeS3Key } from '../utils/fileValidator';
import type {
  UploadJob,
  UploadItem,
  UploadProgressMetrics,
  UploadOptions,
  UploadStatus,
} from '../types/s3';
import type { S3UploadContextType } from '../contexts/S3UploadContext';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Configuration for upload job processing
 */
export interface UploadJobConfig {
  /** Maximum number of concurrent uploads (default: 5) */
  maxConcurrent: number;
  /** Timeout per file in milliseconds (default: 5 minutes) */
  fileTimeout: number;
  /** Maximum retries per file (default: 3) */
  maxRetries: number;
}

/**
 * Worker state for tracking active uploads
 */
interface UploadWorker {
  jobId: string;
  itemId: string;
  abortController: AbortController;
  promise: Promise<void>;
}

// ============================================================================
// Upload Job Manager
// ============================================================================

/**
 * Manages the complete lifecycle of S3 upload jobs
 * Handles file validation, concurrency control, worker queue management,
 * and integration with S3Service and upload context
 */
export class UploadJobManager {
  private s3Service: S3Service;
  private uploadContext: S3UploadContextType;
  private config: UploadJobConfig;
  private activeWorkers: Map<string, UploadWorker> = new Map();
  private workerQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue = false;

  /**
   * Create a new UploadJobManager
   */
  constructor(
    s3Service: S3Service,
    uploadContext: S3UploadContextType,
    config?: Partial<UploadJobConfig>
  ) {
    this.s3Service = s3Service;
    this.uploadContext = uploadContext;
    this.config = {
      maxConcurrent: config?.maxConcurrent ?? 5,
      fileTimeout: config?.fileTimeout ?? 300000, // 5 minutes
      maxRetries: config?.maxRetries ?? 3,
    };
  }

  /**
   * Start a new upload job with validation and queue management
   */
  async startUploadJob(
    files: File[],
    currentPath: string,
    options?: UploadOptions
  ): Promise<string> {
    console.log('[UploadJobManager] Starting upload job', {
      fileCount: files.length,
      currentPath,
      options,
    });

    // Validate files before starting
    const validation = validateFileBatch(files, options);
    if (!validation.isValid) {
      console.error('[UploadJobManager] File validation failed', validation.errors);
      throw new Error(
        `File validation failed: ${validation.errors.join(', ')}`
      );
    }

    // Log warnings if any
    if (validation.warnings.length > 0) {
      console.warn('[UploadJobManager] File validation warnings:', validation.warnings);
    }

    // Create and start the job in context
    console.log('[UploadJobManager] Creating upload job in context');
    const jobId = this.uploadContext.startUpload(files, currentPath, options);
    console.log('[UploadJobManager] Job created with ID:', jobId);
    
    // Wait for the job to be available in the context (React state update is async)
    // Poll for up to 1 second
    let job = this.uploadContext.getJob(jobId);
    let attempts = 0;
    const maxAttempts = 10;
    
    while (!job && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      job = this.uploadContext.getJob(jobId);
      attempts++;
      console.log('[UploadJobManager] Polling for job, attempt:', attempts, 'hasJob:', !!job);
    }
    
    console.log('[UploadJobManager] Retrieved job from context:', { jobId, hasJob: !!job, attempts });

    if (!job) {
      console.error('[UploadJobManager] Failed to retrieve job from context after polling', {
        jobId,
        attempts,
        contextMethods: Object.keys(this.uploadContext),
      });
      throw new Error(`Failed to create upload job - job not found in context after ${attempts} attempts`);
    }

    // Queue the job for processing
    console.log('[UploadJobManager] Queueing job for processing');
    this.queueJobForProcessing(jobId);

    return jobId;
  }

  /**
   * Queue a job for processing with concurrency control
   */
  private queueJobForProcessing(jobId: string): void {
    const job = this.uploadContext.getJob(jobId);
    if (!job) {
      console.error('[UploadJobManager] Cannot queue job - job not found', { jobId });
      return;
    }

    console.log('[UploadJobManager] Queueing job for processing', { 
      jobId, 
      fileCount: job.files.length 
    });

    // Create worker tasks for each file in the job
    for (const item of job.files) {
      const workerTask = async () => {
        console.log('[UploadJobManager] Processing upload item', { jobId, itemId: item.id, fileName: item.file.name });
        await this.processUploadItem(jobId, item.id);
      };

      this.workerQueue.push(workerTask);
    }

    console.log('[UploadJobManager] Worker queue size:', this.workerQueue.length);

    // Start processing queue if not already running
    if (!this.isProcessingQueue) {
      console.log('[UploadJobManager] Starting worker queue processing');
      this.processWorkerQueue();
    } else {
      console.log('[UploadJobManager] Worker queue already processing');
    }
  }

  /**
   * Process the worker queue with concurrency control
   */
  private async processWorkerQueue(): Promise<void> {
    if (this.isProcessingQueue) {
      console.log('[UploadJobManager] Queue already processing, returning');
      return;
    }
    
    this.isProcessingQueue = true;
    console.log('[UploadJobManager] Starting worker queue processing');

    try {
      while (this.workerQueue.length > 0) {
        console.log('[UploadJobManager] Worker queue iteration', {
          queueLength: this.workerQueue.length,
          activeWorkers: this.activeWorkers.size,
          maxConcurrent: this.config.maxConcurrent,
        });

        // Check if we can spawn more workers
        if (this.activeWorkers.size >= this.config.maxConcurrent) {
          console.log('[UploadJobManager] Max concurrent reached, waiting for worker to complete');
          // Wait for at least one worker to complete
          await Promise.race(Array.from(this.activeWorkers.values()).map(w => w.promise));
          continue;
        }

        // Get next task from queue
        const task = this.workerQueue.shift();
        if (!task) {
          console.log('[UploadJobManager] No more tasks in queue');
          break;
        }

        console.log('[UploadJobManager] Starting worker task');
        // Execute task (don't await - let it run concurrently)
        task().catch(err => {
          console.error('[UploadJobManager] Worker task error:', err);
        });
      }

      console.log('[UploadJobManager] Queue drained, waiting for active workers to complete');
      // Wait for all remaining workers to complete
      if (this.activeWorkers.size > 0) {
        await Promise.all(Array.from(this.activeWorkers.values()).map(w => w.promise));
      }
      console.log('[UploadJobManager] All workers completed');
    } finally {
      this.isProcessingQueue = false;
      console.log('[UploadJobManager] Worker queue processing finished');
    }
  }

  /**
   * Process a single upload item
   */
  private async processUploadItem(jobId: string, itemId: string): Promise<void> {
    console.log('[UploadJobManager] processUploadItem called', { jobId, itemId });
    
    const job = this.uploadContext.getJob(jobId);
    if (!job) {
      console.error('[UploadJobManager] Job not found in processUploadItem', { jobId });
      return;
    }

    const item = job.files.find(f => f.id === itemId);
    if (!item) {
      console.error('[UploadJobManager] Item not found in job', { jobId, itemId });
      return;
    }

    console.log('[UploadJobManager] Starting upload for item', { 
      jobId, 
      itemId, 
      fileName: item.file.name,
      fileSize: item.file.size 
    });

    // Create abort controller for this upload
    const abortController = new AbortController();
    const workerId = `${jobId}-${itemId}`;

    // Register active worker
    const workerPromise = this.performUpload(jobId, item, abortController);
    this.activeWorkers.set(workerId, {
      jobId,
      itemId,
      abortController,
      promise: workerPromise,
    });

    console.log('[UploadJobManager] Worker registered', { 
      workerId, 
      activeWorkersCount: this.activeWorkers.size 
    });

    // Clean up when done
    try {
      await workerPromise;
      console.log('[UploadJobManager] Upload completed for item', { jobId, itemId, fileName: item.file.name });
    } catch (err) {
      console.error('[UploadJobManager] Upload failed for item', { jobId, itemId, fileName: item.file.name, error: err });
    } finally {
      this.activeWorkers.delete(workerId);
      console.log('[UploadJobManager] Worker cleaned up', { workerId, activeWorkersCount: this.activeWorkers.size });
    }
  }

  /**
   * Perform the actual file upload with retry logic
   */
  private async performUpload(
    jobId: string,
    item: UploadItem,
    abortController: AbortController,
    attemptCount: number = 0
  ): Promise<void> {
    const job = this.uploadContext.getJob(jobId);
    if (!job) return;

    // Check if job is cancelled
    if (job.isCancelled) {
      this.uploadContext.markItemFailed(jobId, item.id, 'Job was cancelled');
      return;
    }

    // Check if job is paused - wait until resumed
    if (job.isPaused) {
      await this.waitForJobResume(jobId);
      if (job.isCancelled) {
        this.uploadContext.markItemFailed(jobId, item.id, 'Job was cancelled');
        return;
      }
    }

    try {
      console.log('[UploadJobManager] performUpload starting', {
        jobId,
        itemId: item.id,
        fileName: item.file.name,
        fileSize: item.file.size,
        key: item.key,
      });

      // Update item status to uploading
      const uploadingItem: UploadItem = {
        ...item,
        status: 'uploading' as UploadStatus,
        startTime: new Date(),
      };
      this.uploadContext.updateItemProgress(jobId, item.id, uploadingItem);
      console.log('[UploadJobManager] Item status updated to uploading');

      // Perform upload with progress tracking
      let lastUpdate = Date.now();
      const progressCallback = (progress: { loaded: number; total: number }) => {
        const now = Date.now();
        console.log('[UploadJobManager] Progress callback', {
          fileName: item.file.name,
          loaded: progress.loaded,
          total: progress.total,
          percentage: Math.round((progress.loaded / progress.total) * 100),
        });
        if (now - lastUpdate > 100) { // Update max every 100ms
          const updatedItem: UploadItem = {
            ...item,
            status: 'uploading' as UploadStatus,
            progress: Math.round((progress.loaded / progress.total) * 100),
            bytesUploaded: progress.loaded,
            uploadSpeed: progress.loaded / ((now - (item.startTime?.getTime() ?? now)) / 1000),
            lastUpdated: new Date(),
          };
          this.uploadContext.updateItemProgress(jobId, item.id, updatedItem);
          lastUpdate = now;
        }
      };

      // Check if we should use multipart for large files
      const chunkSize = job.progress.totalBytes > 100 * 1024 * 1024 // > 100MB
        ? (5 * 1024 * 1024) // 5MB chunks
        : undefined;

      console.log('[UploadJobManager] Calling s3Service.uploadFile', {
        fileName: item.file.name,
        key: item.key,
        hasS3Service: !!this.s3Service,
        uploadFileType: typeof this.s3Service.uploadFile,
        s3ServiceKeys: Object.keys(this.s3Service),
        s3ServiceMethods: Object.getOwnPropertyNames(Object.getPrototypeOf(this.s3Service)),
      });

      // Perform the upload
      console.log('[UploadJobManager] About to call uploadFile');
      const uploadPromise = this.s3Service.uploadFile(item.file, item.key, progressCallback);
      console.log('[UploadJobManager] uploadFile called, got promise:', uploadPromise);
      
      await Promise.race([
        uploadPromise,
        this.createTimeoutPromise(this.config.fileTimeout, 'Upload timeout'),
        this.createAbortPromise(abortController, 'Upload cancelled'),
      ]);

      console.log('[UploadJobManager] Upload to S3 completed', {
        fileName: item.file.name,
        key: item.key,
      });

      // Mark as completed
      this.uploadContext.markItemCompleted(jobId, item.id);
      console.log('[UploadJobManager] Item marked as completed');

      // Update job progress
      this.updateJobProgress(jobId);
      console.log('[UploadJobManager] Job progress updated');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);

      // Check if we should retry
      if (attemptCount < this.config.maxRetries && this.isRetryableError(error)) {
        // Retry after exponential backoff
        const backoffMs = Math.min(1000 * Math.pow(2, attemptCount), 30000);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        await this.performUpload(jobId, item, abortController, attemptCount + 1);
      } else {
        // Mark as failed
        this.uploadContext.markItemFailed(jobId, item.id, errorMsg);
        this.updateJobProgress(jobId);
      }
    }
  }

  /**
   * Wait for a job to be resumed after pause
   */
  private async waitForJobResume(jobId: string): Promise<void> {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        const job = this.uploadContext.getJob(jobId);
        if (!job || !job.isPaused) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }

  /**
   * Create a timeout promise that rejects after specified time
   */
  private createTimeoutPromise(ms: number, message: string): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    });
  }

  /**
   * Create an abort promise that rejects when abort controller signals
   */
  private createAbortPromise(
    abortController: AbortController,
    message: string
  ): Promise<never> {
    return new Promise((_, reject) => {
      abortController.signal.addEventListener('abort', () => {
        reject(new Error(message));
      });
    });
  }

  /**
   * Check if an error is retryable
   */
  private isRetryableError(error: any): boolean {
    if (!(error instanceof Error)) return false;

    const message = error.message.toLowerCase();
    const retryablePatterns = [
      'network',
      'timeout',
      'econnrefused',
      'econnreset',
      'connection',
      'temporarily',
      'throttling',
      'slowdown',
    ];

    return retryablePatterns.some(pattern => message.includes(pattern));
  }

  /**
   * Update overall job progress metrics
   */
  private updateJobProgress(jobId: string): void {
    const job = this.uploadContext.getJob(jobId);
    if (!job) return;

    const now = new Date();
    const filesUploaded = job.files.filter(f => f.status === 'completed').length;
    const filesFailed = job.files.filter(f => f.status === 'failed').length;
    const bytesUploaded = job.files.reduce((sum, f) => sum + f.bytesUploaded, 0);

    const elapsedMs = now.getTime() - job.progress.startTime.getTime();
    const elapsedSeconds = elapsedMs / 1000;
    const uploadSpeed = elapsedSeconds > 0 ? bytesUploaded / elapsedSeconds : 0;

    let estimatedTimeRemaining: number | null = null;
    if (uploadSpeed > 0 && bytesUploaded < job.progress.totalBytes) {
      estimatedTimeRemaining = Math.ceil(
        (job.progress.totalBytes - bytesUploaded) / uploadSpeed
      );
    }

    const metrics: UploadProgressMetrics = {
      totalFiles: job.files.length,
      filesUploaded,
      filesFailed,
      totalBytes: job.progress.totalBytes,
      bytesUploaded,
      uploadSpeed,
      estimatedTimeRemaining,
      percentage:
        job.progress.totalBytes > 0
          ? Math.min(100, Math.round((bytesUploaded / job.progress.totalBytes) * 100))
          : 0,
      startTime: job.progress.startTime,
      currentTime: now,
    };

    this.uploadContext.updateJobProgress(jobId, metrics);

    // Check if job is complete
    if (filesUploaded + filesFailed === job.files.length) {
      const success = filesFailed === 0;
      this.uploadContext.completeJob(jobId, success);
    }
  }

  /**
   * Pause an upload job
   */
  async pauseJob(jobId: string): Promise<void> {
    this.uploadContext.pauseJob(jobId);

    // Pause all active workers for this job
    for (const [workerId, worker] of this.activeWorkers) {
      if (worker.jobId === jobId) {
        worker.abortController.abort();
      }
    }
  }

  /**
   * Resume a paused upload job
   */
  async resumeJob(jobId: string): Promise<void> {
    const job = this.uploadContext.getJob(jobId);
    if (!job) return;

    this.uploadContext.resumeJob(jobId);

    // Re-queue pending items
    for (const item of job.files) {
      if (item.status === 'pending') {
        const workerTask = async () => {
          await this.processUploadItem(jobId, item.id);
        };
        this.workerQueue.push(workerTask);
      }
    }

    // Resume processing queue
    if (!this.isProcessingQueue) {
      this.processWorkerQueue();
    }
  }

  /**
   * Cancel an upload job
   */
  async cancelJob(jobId: string): Promise<void> {
    this.uploadContext.cancelJob(jobId);

    // Cancel all active workers for this job
    for (const [workerId, worker] of this.activeWorkers) {
      if (worker.jobId === jobId) {
        worker.abortController.abort();
      }
    }

    // Remove pending items from queue
    this.workerQueue = this.workerQueue.filter(task => {
      // This is a simple approach - in production might want better tracking
      return true;
    });
  }

  /**
   * Retry a failed file
   */
  async retryFile(jobId: string, itemId: string): Promise<void> {
    const job = this.uploadContext.getJob(jobId);
    if (!job) return;

    const item = job.files.find(f => f.id === itemId);
    if (!item || item.status !== 'failed') return;

    // Reset item to pending
    this.uploadContext.retryFile(jobId, itemId);

    // Re-queue for processing
    const workerTask = async () => {
      await this.processUploadItem(jobId, itemId);
    };
    this.workerQueue.push(workerTask);

    // Resume processing queue
    if (!this.isProcessingQueue) {
      this.processWorkerQueue();
    }
  }

  /**
   * Get active worker count
   */
  getActiveWorkerCount(): number {
    return this.activeWorkers.size;
  }

  /**
   * Get queued task count
   */
  getQueuedTaskCount(): number {
    return this.workerQueue.length;
  }

  /**
   * Shutdown the manager and cancel all uploads
   */
  async shutdown(): Promise<void> {
    // Abort all active workers
    for (const worker of this.activeWorkers.values()) {
      worker.abortController.abort();
    }

    // Clear queue
    this.workerQueue = [];

    // Wait for all workers to complete
    if (this.activeWorkers.size > 0) {
      await Promise.all(
        Array.from(this.activeWorkers.values()).map(w => w.promise.catch(() => {}))
      );
    }
  }
}

// ============================================================================
// Export types and utilities
// ============================================================================

/**
 * Create an upload job manager instance
 */
export function createUploadJobManager(
  s3Service: S3Service,
  uploadContext: S3UploadContextType,
  config?: Partial<UploadJobConfig>
): UploadJobManager {
  return new UploadJobManager(s3Service, uploadContext, config);
}
